// Pure capability resolution: explicit override ∪ DB grant ∪ role-derived
// default → effective `HasCapabilityFn`.
//
// Slice RP-2b-i / SC-2 / SC-4 of docs/planning/firefly_role_context_presentation.md.
// Precedence: localStorage override > admin DB grant > role-derived default.

import type { Capability, HasCapabilityFn } from "./capabilities.js";
import type { CapabilityOverrides } from "./capabilityOverrides.js";
import { mergeCapabilitySources } from "./capabilityMerge.js";

export type HasRoleProbe = (...roles: string[]) => boolean;

/**
 * Role-derived default when no explicit override or DB grant exists. §9 Q2
 * capabilities; each default is minimal and overridable via Settings / admin.
 */
export function defaultForCapability(
  capability: Capability,
  hasRole: HasRoleProbe,
): boolean {
  switch (capability) {
    case "is_ads_runner":
      return hasRole("sales");
    case "is_kb_author":
      return hasRole("trainer", "ads_guidance_editor", "platform_trainer");
    case "is_dispatcher":
      return hasRole("logistics_coordinator", "dispatcher");
    case "is_floor_operator":
      return false;
    case "is_cogs_author":
      return false;
    case "is_personal_assistant":
      if (hasRole("owner", "org_owner")) return true;
      if (hasRole("admin", "org_admin")) return false;
      return false;
    default: {
      const _exhaustive: never = capability;
      return _exhaustive;
    }
  }
}

export function isCapabilityEffective(
  capability: Capability,
  overrides: CapabilityOverrides,
  hasRole: HasRoleProbe,
  dbGrants: CapabilityOverrides = {},
): boolean {
  return mergeCapabilitySources({
    localOverride: overrides[capability],
    dbGrant: dbGrants[capability],
    roleDefault: defaultForCapability(capability, hasRole),
  });
}

export function resolveCapabilities(
  overrides: CapabilityOverrides,
  hasRole: HasRoleProbe,
  dbGrants: CapabilityOverrides = {},
): HasCapabilityFn {
  return (...probe: Capability[]) =>
    probe.some((cap) => isCapabilityEffective(cap, overrides, hasRole, dbGrants));
}
